import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import RadioGroupField from 'components/RadioGroupField';
import { USER_SEARCH_PREFERENCE, User } from 'models/User';
import useOperationStepValues from 'router/data/useOperationStepValues';
import ResolveMultiSelectField from 'components/ResolveMultiSelectField';
import CheckToggleField from 'components/CheckToggleField';
import ListingCreationSelectField from 'router/subrouters/ListingCreationWizard/components/ListingCreationSelectField';
import validations from 'validations';
import InputField from 'components/InputField';
import { numberIsEqual } from '../../utils';
import CurrentProfileContext from '../../providers/CurrentProfile/CurrentProfile.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './Preferences.styles.scss';
import './Preferences.styles.responsive.scss';

type PreferencesProps = {
  className?: string;
  detailed?: boolean;
};

const Preferences: React.FC<PreferencesProps> = (props) => {
  const { className, detailed } = props;
  const { t } = useTranslation();

  const { industrySelectOptions, locationOptions } = useOperationStepValues();

  const { values } = useFormState<User>();
  const { change } = useForm();

  const {
    hasAcquisitionPreferences,
    preferredIndustryIncludeMode,
    preferredLocationMode,
    preferredBudgetMode,
    preferredIndustryExcludeMode,
  } = values;

  const classes = classNames('preferences', className);

  const disabled = !hasAcquisitionPreferences;

  const { isMyProfile } = useContext(CurrentProfileContext);
  const {
    currentUser: { hasVerifiedEmail },
    openVerificationModal,
  } = useContext(CurrentUserContext);

  const shouldOpenEmailVerificationModal = useMemo(
    () => isMyProfile && !hasVerifiedEmail && !hasAcquisitionPreferences,
    [hasAcquisitionPreferences, hasVerifiedEmail, isMyProfile],
  );

  return (
    <div className={classes}>
      {detailed && <p>{t('acquisitionPreferences')}</p>}

      <Field
        name="hasAcquisitionPreferences"
        type="checkbox"
        component={CheckToggleField}
        label={<span>{t('acquisitionPreferencesToggle')}</span>}
        {...(shouldOpenEmailVerificationModal && {
          onChange: () => openVerificationModal(),
        })}
      />

      <div className="preferences__fields">
        <div className="preferences__form-row">
          <div className="preferences__form-radio-field">
            <h2 className="preferences__form-radio-field__title">
              {t('location')}
            </h2>
            <RadioGroupField
              name="preferredLocationMode"
              options={[
                {
                  label: t('anyLocation'),
                  value: USER_SEARCH_PREFERENCE.ANY,
                },
                {
                  label: t('specificLocations'),
                  value: USER_SEARCH_PREFERENCE.SPECIFIC,
                },
              ]}
              disabled={disabled}
            />
            {preferredLocationMode === 'specific' && (
              <Field
                name="preferredLocations"
                options={locationOptions}
                component={ListingCreationSelectField}
                withShowMore={false}
                disabled={disabled}
                validate={validations.required()}
              />
            )}
          </div>
          <div className="preferences__form-radio-field">
            <h2 className="preferences__form-radio-field__title">
              {t('priceRangeBudge')}
            </h2>
            <RadioGroupField
              name="preferredBudgetMode"
              options={[
                {
                  label: t('anyPriceRange'),
                  value: USER_SEARCH_PREFERENCE.ANY,
                },
                {
                  label: t('specificPriceRange'),
                  value: USER_SEARCH_PREFERENCE.SPECIFIC,
                },
              ]}
              disabled={disabled}
            />
            {preferredBudgetMode === 'specific' && (
              <div className="lcw-page__field-row">
                <Field
                  disabled={disabled}
                  name="budgetMinPrice"
                  label={t('minPrice')}
                  numberStyle="dottedNumber"
                  isEqual={numberIsEqual}
                  component={InputField}
                  placeholder="$"
                  validate={validations.composeValidators(
                    validations.positiveNumbersOnly(
                      t('onlyPositiveNumbersAreAllowed'),
                    ),
                  )}
                />
                <Field
                  disabled={disabled}
                  name="budgetMaxPrice"
                  label={t('maxPrice')}
                  numberStyle="dottedNumber"
                  isEqual={numberIsEqual}
                  component={InputField}
                  placeholder="$"
                  validate={validations.composeValidators(
                    validations.positiveNumbersOnly(
                      t('onlyPositiveNumbersAreAllowed'),
                    ),
                    validations.isGreatherThanField('amountFrom'),
                  )}
                />
              </div>
            )}
          </div>
        </div>
        <div className="preferences__form-row">
          <div className="preferences__form-radio-field">
            <h2 className="preferences__form-radio-field__title">
              {t('industriesToInclude')}
            </h2>
            <RadioGroupField
              name="preferredIndustryIncludeMode"
              options={[
                {
                  label: t('anyIndustry'),
                  value: USER_SEARCH_PREFERENCE.ANY,
                },
                {
                  label: t('specificIndustries'),
                  value: USER_SEARCH_PREFERENCE.SPECIFIC,
                },
              ]}
              onChange={(val) => {
                if (val.target.value === USER_SEARCH_PREFERENCE.SPECIFIC) {
                  if (
                    preferredIndustryExcludeMode ===
                    USER_SEARCH_PREFERENCE.SPECIFIC
                  )
                    change(
                      'preferredIndustryExcludeMode',
                      USER_SEARCH_PREFERENCE.ANY,
                    );
                }
                change('preferredIndustryIncludeMode', val.target.value);
              }}
              disabled={disabled}
            />
            {preferredIndustryIncludeMode ===
              USER_SEARCH_PREFERENCE.SPECIFIC && (
              <Field
                renderAsPortal
                name="preferredIncludeIndustries"
                options={industrySelectOptions}
                component={ResolveMultiSelectField}
                disabled={disabled}
              />
            )}
          </div>
          <div className="preferences__form-radio-field">
            <h2 className="preferences__form-radio-field__title">
              {t('industriesToExclude')}
            </h2>
            <RadioGroupField
              name="preferredIndustryExcludeMode"
              options={[
                {
                  label: t('anyIndustry'),
                  value: USER_SEARCH_PREFERENCE.ANY,
                },
                {
                  label: t('specificIndustries'),
                  value: USER_SEARCH_PREFERENCE.SPECIFIC,
                },
              ]}
              onChange={(val) => {
                if (val.target.value === USER_SEARCH_PREFERENCE.SPECIFIC) {
                  if (
                    preferredIndustryIncludeMode ===
                    USER_SEARCH_PREFERENCE.SPECIFIC
                  )
                    change(
                      'preferredIndustryIncludeMode',
                      USER_SEARCH_PREFERENCE.ANY,
                    );
                }
                change('preferredIndustryExcludeMode', val.target.value);
              }}
              disabled={disabled}
            />
            {preferredIndustryExcludeMode ===
              USER_SEARCH_PREFERENCE.SPECIFIC && (
              <Field
                renderAsPortal
                name="preferredExcludeIndustries"
                options={industrySelectOptions}
                component={ResolveMultiSelectField}
                disabled={disabled}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preferences;
